import { Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast } from "@/comn/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    APIS,
    PGEUIDS,
    SF_AUTH_MGMT_DTL,
    SG_AUTH_LANG_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/auth/services/OsciAuthService";
import { comnUtils } from "@/comn/utils";
import React, { useEffect } from "react";
import { SG_AUTH_MENU_LIST } from "@/tz/go/egaz/osci/ptl/ptl/menu/services/OsciAuthMenuService";
import { OSCI0104013Q } from "@/tz/go/egaz/osci/ptl/ptl/auth/OSCI0104013Q";

/*
 * Auth Management Registration
 * !== 권한 관리 등록 ==!
 */
export const OSCI0104012S = () => {
    const { pgeUid } = usePage({
        auth: ["auth_osci0003"],
    });
    const { authId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const modal = useModal();
    const { setStore, getStore } = useStore({ pgeUid: pgeUid });
    const langCodes = ["KO", "EN", "TZ"];
    const { t } = useTranslation();

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0095" },
        ],
    });

    const form = {
        authDtl: useForm({ defaultSchema: SF_AUTH_MGMT_DTL }),
    };

    const grid = {
        authLangList: useGrid({ defaultSchema: SG_AUTH_LANG_LIST }),
        authMenuList: useGrid({ defaultSchema: SG_AUTH_MENU_LIST }),
    };

    const fetch = {
        getAuthDtl: useFetch({
            api: (data: any) => APIS.getAuthDtl(data),
            onSuccess: (data: any) => {
                const { content } = data.authMgmtDtl;
                if (comnUtils.isEmpty(content)) {
                    modal.openModal({ content: "msg.osc.00201" }); // Abnormal access. !== 비정상 접근입니다. ==!
                    handler.onListBtnClick();
                }

                const { authLangList } = content;
                form.authDtl.setValues(content);

                if (!comnUtils.isEmpty(authLangList)) {
                    authLangList.forEach((item: any) => {
                        const { langCd, authNm, authDesc } = item;
                        form.authDtl.setValue(`authNm${langCd}`, authNm);
                        form.authDtl.setValue(`authDesc${langCd}`, authDesc);
                    });
                }

                grid.authMenuList.setData({ content: content.cnctList });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        updateAuthDtl: useFetch({
            api: () =>
                APIS.updateAuthDtl({
                    ...form.authDtl.getValues(),
                    authLangList: grid.authLangList.getData(),
                    cnctList: grid.authMenuList.getData({ excludes: ["origin"] }),
                }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006",
                    close: false,
                    onConfirm: () => {
                        handler.onListBtnClick();
                    },
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00045" }), // 저장이 실패하였습니다.
        }),
        deleteAuthDtl: useFetch({
            api: () => APIS.deleteAuthDtl(form.authDtl.getValues()),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00060",
                    close: false,
                    onConfirm: () => {
                        handler.onListBtnClick();
                    },
                });
            },
            onError: (error: any) => {
                const { errorMessage } = error.response.data;
                modal.openModal({ content: errorMessage });
            },
        }),

        // Select Auth user mapping !== 권한한 사용자 매핑 정보 조회 ==!
        getAuthMapInfo: useFetch({
            api: (data: any) => APIS.getAuthMapInfo(data),
            onSuccess: (data: any) => {
                const authMapInfo = data.authMapInfo.content;
                if (authMapInfo.menuAuthMapYn === "Y") {
                    modal.openModal({ content: t("msg.osc.00259", { 0: t("L_AUTH"), 1: t("L_MENU_ID") }) }); // If there is even one menu mapped to the {0}, it cannot be deleted. !== {0}에 {1}가 하나라도 매핑되어 있는 경우 삭제가 불가능합니다. ==!
                    return;
                }

                if (authMapInfo.usrAuthMapYn === "Y") {
                    modal.openModal({ content: t("msg.osc.00261", { 0: t("L_AUTH") }) }); // Deletion is not possible if at least one user with that {0} is mapped. !== 해당 {0}을 가진 사용자가 한명이라도 매핑되어 있는 경우 삭제가 불가능합니다. ==!
                    return;
                }

                fetch.deleteAuthDtl.fetch();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00040" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),
    };

    const handler = {
        onGridAddBtnClick: () => {
            const modalCallback = (list: any[]) => {
                if (!comnUtils.isEmpty(list)) {
                    list.forEach((item: any) => grid.authMenuList.addRow({ ...item, authId: authId }));
                }
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "md",
                content: (
                    <OSCI0104013Q
                        authId={authId}
                        bsopClsfCd={form.authDtl.getValue("bsopClsfCd")}
                        ptlTpCd={form.authDtl.getValue("ptlTpCd")}
                        callback={modalCallback}
                        authMenuList={grid.authMenuList.getData()}
                    />
                ),
            });
        },
        onGridDelBtnClick: () => {
            if (!grid.authMenuList.isChecked()) {
                modal.openModal({ content: "msg.osc.00193" }); // Please select a destination to delete. !== 삭제대상을 선택하세요. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062",
                onConfirm: () => {
                    grid.authMenuList.deleteRow("checkbox");
                },
            });
        },
        onDelBtnClick: () => {
            modal.openModal({
                content: "msg.osc.00062", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    fetch.getAuthMapInfo.fetch({
                        authId: form.authDtl.getValue("authId"),
                        ptlTpCd: form.authDtl.getValue("ptlTpCd"),
                    });
                },
            });
        },
        onSaveBtnClick: () => {
            modal.openModal({
                content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => {
                    form.authDtl.handleSubmit(
                        () => {
                            grid.authLangList.clearData();

                            langCodes.forEach((langCd: any) => {
                                const authNm = form.authDtl.getValue(`authNm${langCd}`);
                                const authDesc = form.authDtl.getValue(`authDesc${langCd}`);

                                grid.authLangList.addRow({
                                    langCd: langCd,
                                    authNm: authNm,
                                    authDesc: authDesc,
                                    authId: authId,
                                });
                            });

                            fetch.updateAuthDtl.fetch();
                        },
                        () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid !== 데이터가 유효하지 않습니다. ==!
                    )();
                },
            });
        },
        onListBtnClick: () => navigate(`${URLS.osci0104010q}`),
    };

    const render = {
        authMenuList: {
            cell: {},
        },
    };

    useEffect(() => {
        form.authDtl.setEditable("bsopClsfCd", false);
        form.authDtl.setEditable("ptlTpCd", false);
        form.authDtl.setEditable("authId", false);

        const mainStore = getStore(PGEUIDS.osci0104010q);
        if (comnUtils.isEmpty(mainStore) || comnUtils.isEmpty(mainStore.param)) {
            modal.openModal({ content: "msg.osc.00201" }); // Abnormal access. !== 비정상 접근입니다. ==!
            handler.onListBtnClick();
            return;
        }
        fetch.getAuthDtl.fetch(mainStore.param);

        return () => {
            const { param, ...rest } = mainStore;
            setStore(PGEUIDS.osci0104010q, rest);
        };
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.authDtl.schema.bsopClsfCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.authDtl.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.authDtl.schema.authId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.authDtl.schema.authNmTZ} />
                            <Group.Control {...form.authDtl.schema.authDescTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.authDtl.schema.authNmEN} />
                            <Group.Control {...form.authDtl.schema.authDescEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.authDtl.schema.authNmKO} />
                            <Group.Control {...form.authDtl.schema.authDescKO} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section hidden={true}>
                        <Grid {...grid.authLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Layout>
                            <Layout.Right>
                                <Layout.Button role={"gridAdd"} onClick={handler.onGridAddBtnClick} />
                                <Layout.Button role={"gridDelete"} onClick={handler.onGridDelBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Section>
                    <Group.Section>
                        <Grid {...grid.authMenuList.grid} render={render.authMenuList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onSaveBtnClick} />
                    {/* <Layout.Button role={"delete"} onClick={handler.onDelBtnClick} /> */}
                </Layout.Right>
            </Layout>
        </Page>
    );
};
